/**
 * SetupChecklist — the first-login guide for a new company.
 *
 * Reads REAL state (company settings, mail carriers, client count) and shows
 * the three things that stand between a brand-new operator and their first
 * mailed dispute. Each step is one click to the right screen. Dismissible;
 * disappears on its own once everything is done. Also renders the compact
 * "readiness" line so they always know what's still missing.
 *
 * Deliberately calm — no overlays, no forced tour. It's a checklist, not a
 * modal maze.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ImageIcon, Send, UserPlus, X, Sparkles } from "lucide-react";
import { company as companyApi, request } from "../lib/api";

const DISMISS_KEY = "nedm.setup.dismissed";

interface Provider { id: string; configured: boolean }

export function SetupChecklist({ clientCount, onLoadDemo }: { clientCount: number; onLoadDemo?: () => void }) {
  const nav = useNavigate();
  const [dismissed, setDismissed] = useState<boolean>(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [logo, setLogo] = useState<boolean | null>(null);
  const [businessName, setBusinessName] = useState<boolean | null>(null);
  const [autoMail, setAutoMail] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await companyApi.get();
        if (cancelled) return;
        setLogo(!!r.settings?.logo_url);
        setBusinessName(!!r.settings?.business_name);
      } catch { /* leave unknown */ }
      try {
        const m = await request<{ providers?: Provider[] }>("/api/mail-jobs", { query: { limit: 1 } });
        if (cancelled) return;
        setAutoMail(!!m.providers?.some((p) => p.id !== "manual" && p.configured));
      } catch { /* leave unknown */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const steps = [
    {
      id: "brand",
      icon: ImageIcon,
      title: "Put your brand on it",
      detail: "Business name + logo — this is what your clients see on intake, portal, and every email.",
      done: !!(logo && businessName),
      cta: "Open Company Settings",
      go: () => nav("/settings"),
    },
    {
      id: "mail",
      icon: Send,
      title: "Choose how you'll mail letters",
      detail: autoMail
        ? "Automatic mailing is connected."
        : "You can print and mail letters yourself today (Manual). Connect Lob or LetterStream later for one-click automatic mailing.",
      done: true,               // Manual always works — this step never blocks
      optional: !autoMail,
      cta: autoMail ? "Mailing settings" : "Connect automatic mailing (optional)",
      go: () => nav("/settings"),
    },
    {
      id: "client",
      icon: UserPlus,
      title: "Add your first client",
      detail: "Add a real client, or load a demo client to see the whole pipeline — report, findings, letters, bundles — instantly.",
      done: clientCount > 0,
      cta: "Add a client",
      go: () => nav("/clients/new"),
    },
  ];

  const remaining = steps.filter((s) => !s.done).length;
  const allDone = remaining === 0;

  if (dismissed && !allDone) {
    // Compact readiness line only.
    return (
      <div className="muted small" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Circle size={12} aria-hidden="true" />
        <span>{remaining} setup step{remaining === 1 ? "" : "s"} left</span>
        <button className="btn-link" onClick={() => { localStorage.removeItem(DISMISS_KEY); setDismissed(false); }}>Show</button>
      </div>
    );
  }
  if (allDone) {
    return (
      <div className="muted small" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, color: "var(--ne-success)" }}>
        <CheckCircle2 size={14} aria-hidden="true" />
        <span>You're set up — {autoMail ? "automatic mailing connected" : "mailing manually until you connect a carrier"}.</span>
      </div>
    );
  }

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-header" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h2 style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Sparkles size={16} aria-hidden="true" /> Get set up in three steps
        </h2>
        <span className="muted small" style={{ marginLeft: "auto" }}>{steps.length - remaining} of {steps.length} done</span>
        <button className="btn-secondary" aria-label="Hide setup guide" onClick={() => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); }} style={{ padding: "4px 8px" }}>
          <X size={14} aria-hidden="true" />
        </button>
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.id} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "10px 12px", border: "1px solid var(--ne-silver-1)", opacity: s.done ? 0.7 : 1 }}>
              <div style={{ color: s.done ? "var(--ne-success)" : "var(--ne-red)", marginTop: 2, flexShrink: 0 }}>
                {s.done ? <CheckCircle2 size={20} aria-hidden="true" /> : <Icon size={20} aria-hidden="true" />}
              </div>
              <div style={{ flex: 1 }}>
                <strong>{i + 1}. {s.title}</strong>{s.optional && !s.done ? <span className="muted small"> · optional</span> : null}
                <div className="muted small" style={{ marginTop: 2 }}>{s.detail}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                {s.id === "client" && !s.done && onLoadDemo && (
                  <button className="btn-secondary" onClick={onLoadDemo}>Load a demo client</button>
                )}
                {!s.done && <button className="btn-primary" onClick={s.go}>{s.cta}</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
